import {
  type Control,
  type FieldErrors,
  useFieldArray,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form';
import type { QuizQuestionFormInput } from 'features/quizzes/components/quiz-form/QuizQuestionForm';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import type { DragEndEvent } from '@dnd-kit/core/dist/types';
import { Box, Button, FormHelperText, Stack } from '@mui/material';

import { DynamicChoice } from 'features/quizzes/components/quiz-form/DynamicChoice';
import InputLabel from 'components/InputLabel';
import { useTranslation } from 'react-i18next';

const CHOICES_MAX_LIMIT = 30;
const CHOICE_HEIGHT = 38;

// Calculate max height for scrollable choices container
const CHOICE_ITEM_SPACING = 24;
const MAX_VISIBLE_CHOICES = 4;
const CHOICE_WITH_SPACING = CHOICE_HEIGHT + CHOICE_ITEM_SPACING;
const CHOICES_CONTAINER_MAX_HEIGHT =
  CHOICE_WITH_SPACING * MAX_VISIBLE_CHOICES - CHOICE_ITEM_SPACING;

type ChoicesProps = {
  control: Control<QuizQuestionFormInput>;
  setValue: UseFormSetValue<QuizQuestionFormInput>;
  errors: FieldErrors<QuizQuestionFormInput>;
  watch: UseFormWatch<QuizQuestionFormInput>;
  multipleChoices: boolean;
};

const Choices = ({ control, setValue, watch, errors, multipleChoices }: ChoicesProps) => {
  const { t } = useTranslation('quizzes');

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'choices',
  });

  const choices = watch('choices');

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const addChoice = () => {
    append({ text: '', correct: false });
  };

  const deleteChoice = (index: number) => {
    remove(index);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over === null || active.id === over.id) {
      return;
    }

    let oldIndex = -1;
    let newIndex = -1;

    fields.forEach((field, index) => {
      if (field.id === active.id) {
        oldIndex = index;
      } else if (field.id === over.id) {
        newIndex = index;
      }
    });

    if (oldIndex !== -1 && newIndex !== -1) {
      move(oldIndex, newIndex);
    }
  };

  // Handle radio button change manually - uncheck all others when one is selected
  const handleRadioChange = (index: number, checked: boolean) => {
    if (checked) {
      choices.forEach((_, i) => {
        if (i !== index) {
          setValue(`choices.${i}.correct`, false);
        }
      });
      setValue(`choices.${index}.correct`, true, {
        shouldValidate: true,
      });
    }
  };

  return (
    <div>
      <Box
        sx={{
          mb: 3,
        }}
      >
        <InputLabel
          sx={{
            mb: 1.5,
          }}
        >
          {t('polls:poll_form.choices.label')}
        </InputLabel>
        <FormHelperText>{t('quiz_form.questions.choices.description')}</FormHelperText>
      </Box>
      <Box
        sx={{
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            maxHeight: CHOICES_CONTAINER_MAX_HEIGHT,
            overflowY: 'auto',
          }}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToParentElement]}
          >
            <SortableContext items={fields} strategy={verticalListSortingStrategy}>
              <Stack spacing={6}>
                {fields.map((item, index) => (
                  <DynamicChoice
                    key={item.id}
                    id={item.id}
                    control={control}
                    index={index}
                    errors={errors}
                    deleteChoice={deleteChoice}
                    multiple={multipleChoices}
                    onChange={handleRadioChange}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
        </Box>
      </Box>
      <Button
        sx={{
          mt: choices.length ? 6 : 0,
          fontWeight: 400,
        }}
        type="button"
        variant="outlined"
        onClick={addChoice}
        disabled={choices.length === CHOICES_MAX_LIMIT}
      >
        {t('polls:poll_form.choices.btn_add_choice')}
      </Button>
    </div>
  );
};

export default Choices;
